import { BomDetail } from '../services/bomDetailApi';
import { MaterialIssue } from '../services/materialIssueApi';
import { WorkOrder } from '../services/workOrderApi';

export const toComponentKey = (type: 'rawItem' | 'endgood', id?: number | null) =>
  id ? `${type}:${id}` : '';

export const getWorkOrderEndgoodId = (workOrder: WorkOrder) =>
  workOrder.endgoodId ?? workOrder.endgood?.id;

export const getMaterialIssueComponentKey = (issue: MaterialIssue) => {
  const rawItemId = issue.rawItem?.id ?? (issue as MaterialIssue & { rawItemId?: number }).rawItemId;
  if (rawItemId) return toComponentKey('rawItem', rawItemId);

  const endgoodId = issue.endgood?.id ?? (issue as MaterialIssue & { endgoodId?: number }).endgoodId;
  if (endgoodId) return toComponentKey('endgood', endgoodId);

  return '';
};

export const getBomDetailComponentKey = (detail: BomDetail) => {
  const rawItemId = detail.rawItem?.id;
  if (rawItemId) return toComponentKey('rawItem', rawItemId);

  const endgoodId = detail.endgood?.id ?? detail.endgoodId;
  return toComponentKey('endgood', endgoodId);
};

export const aggregateMaterialIssued = (issues: MaterialIssue[]): Record<string, number> => {
  const map: Record<string, number> = {};
  issues.forEach((issue) => {
    const key = getMaterialIssueComponentKey(issue);
    if (!key) return;
    map[key] = (map[key] || 0) + (issue.issueQuantity ?? 0);
  });
  return map;
};

export const aggregateMaterialRemaining = (issues: MaterialIssue[]): Record<string, number> => {
  const map: Record<string, number> = {};
  issues.forEach((issue) => {
    const key = getMaterialIssueComponentKey(issue);
    if (!key) return;
    const remaining =
      (issue.issueQuantity ?? 0) -
      (issue.consumedQuantity ?? 0) -
      (issue.returnedQuantity ?? 0) -
      (issue.wastedQuantity ?? 0);
    map[key] = (map[key] || 0) + Math.max(0, remaining);
  });
  return map;
};

export type StoreMaterialStatus = 'sufficient' | 'shortfall' | 'not_issued';

export interface StoreMaterialRow {
  id: number;
  processName: string;
  partName: string;
  unit: string;
  quantityRemaining: number;
  quantityNeeded: number;
  shortfall: number;
  status: StoreMaterialStatus;
  rawItemId?: number;
  isPurchasable: boolean;
}

export const getStoreMaterialStatus = (
  quantityRemaining: number,
  quantityNeeded: number
): StoreMaterialStatus => {
  if (quantityRemaining <= 0) return 'not_issued';
  if (quantityRemaining >= quantityNeeded) return 'sufficient';
  return 'shortfall';
};

export const buildStoreMaterialRows = (
  bomDetails: BomDetail[],
  materialRemainingByKey: Record<string, number>,
  workOrderQuantity: number
): StoreMaterialRow[] => {
  const lotQty = workOrderQuantity > 0 ? workOrderQuantity : 0;

  return bomDetails
    .filter((detail) => detail.rawItem?.id)
    .map((detail) => {
      const componentKey = getBomDetailComponentKey(detail);
      const perUnitQty = detail.quantity ?? detail.totalQuantity ?? 0;
      const quantityNeeded = perUnitQty * lotQty;
      const quantityRemaining = materialRemainingByKey[componentKey] ?? 0;
      const shortfall = Math.max(0, quantityNeeded - quantityRemaining);

      return {
        id: detail.id,
        processName: detail.process?.processName || 'Unassigned',
        partName: detail.rawItem?.itemName || 'Unknown part',
        unit: detail.unit || 'pc',
        quantityRemaining,
        quantityNeeded,
        shortfall,
        status: getStoreMaterialStatus(quantityRemaining, quantityNeeded),
        rawItemId: detail.rawItem?.id,
        isPurchasable: Boolean(detail.rawItem?.id),
      };
    });
};

export const workOrderNeedsMaterialAttention = (rows: StoreMaterialRow[]): boolean =>
  rows.some((row) => row.status !== 'sufficient');

export const countMaterialAttentionLines = (rows: StoreMaterialRow[]): number =>
  rows.filter((row) => row.status !== 'sufficient').length;
